import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { SEO } from "@/components/SEO";
import { SectionHeading } from "@/components/SectionHeading";
import { images, services } from "@/data/content";

const serviceImages = [images.family, images.retirement, images.meeting];

const Services = () => (
  <>
    <SEO
      title="Services | Bello Joy Consulting"
      description="Life insurance, health insurance, retirement planning, family protection, business insurance, financial consulting, and legacy planning."
    />
    <PageHero
      testId="services-page-hero"
      kicker="Services"
      title="Guidance for every"
      accent="season of life."
      description="Bello Joy Consulting helps individuals, families, and businesses make informed protection decisions with warmth, clarity, and care."
      image={images.retirement}
      imageAlt="A couple walking together with confidence"
    />

    <section data-testid="services-detail-section" className="px-6 pb-24 md:pb-32">
      <div className="mx-auto max-w-7xl">
        <SectionHeading
          testId="services-detail-heading"
          kicker="Personalized protection"
          title="Clear options. Honest guidance. No pressure."
          description="Each service begins with a conversation about your responsibilities, hopes, and concerns—then becomes a strategy you can understand and trust."
        />

        <div className="space-y-8">
          {services.map((service, index) => {
            const Icon = service.icon;
            const image = serviceImages[index % serviceImages.length];
            return (
              <Reveal key={service.slug} data-testid={`service-detail-${service.slug}`}>
                <article id={service.slug} className="scroll-mt-28 overflow-hidden rounded-[2rem] border border-border bg-card shadow-soft">
                  <div className={`grid lg:grid-cols-[0.9fr_1.1fr] ${index % 2 ? "lg:[&>*:first-child]:order-2" : ""}`}>
                    <div className="image-frame min-h-[320px] overflow-hidden">
                      <img src={image} alt={`${service.title} guidance`} className="h-full w-full object-cover" loading="lazy" />
                    </div>
                    <div className="p-8 md:p-12">
                      <div className="mb-8 flex items-center justify-between gap-6">
                        <span className="font-serif text-5xl italic text-secondary">{service.number}</span>
                        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-muted text-primary">
                          <Icon size={24} />
                        </span>
                      </div>
                      <h2 className="text-3xl tracking-tight text-primary md:text-4xl">{service.title}</h2>
                      <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">{service.description}</p>
                      <div className="mt-7 grid gap-3" data-testid={`service-points-${service.slug}`}>
                        {service.points.map((point) => (
                          <div key={point} className="flex items-start gap-3 text-sm text-primary/85">
                            <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-secondary" />
                            <span>{point}</span>
                          </div>
                        ))}
                      </div>
                      <Button asChild variant="outline" className="mt-8 rounded-full border-primary/25 bg-transparent px-6 text-primary hover:bg-secondary/20">
                        <Link to={`/contact?service=${encodeURIComponent(service.title)}`} data-testid={`service-consultation-${service.slug}`}>
                          Discuss {service.title} <ArrowRight size={16} />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>

    <section data-testid="services-cta" className="px-6 pb-24 md:pb-32">
      <Reveal className="mx-auto max-w-7xl rounded-[2rem] border border-secondary/40 bg-secondary/20 p-8 md:p-12">
        <div className="grid gap-8 lg:grid-cols-[1fr_auto] lg:items-center">
          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.28em] text-primary/70">Unsure where to begin?</p>
            <h2 className="text-3xl tracking-tight text-primary md:text-5xl">Start with your story, not a product.</h2>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
              Alicia will listen first, explain your options clearly, and help you choose the next right step for your family or business.
            </p>
          </div>
          <Button asChild size="lg" className="rounded-full bg-primary px-8 text-primary-foreground transition-[transform,background-color] duration-300 hover:-translate-y-1 hover:bg-primary/90">
            <Link to="/contact" data-testid="services-cta-button">Schedule a Consultation <ArrowRight size={17} /></Link>
          </Button>
        </div>
      </Reveal>
    </section>
  </>
);

export default Services;
